import React, {useContext, useEffect} from "react";
import CustomPopup from "../../../../components/CustomPopup";
import {ThemeContext} from "../../../../providers/themeContext/theme-context.tsx";
import './MedalDialog.css'
import {apiService} from "../../../../common/utils/http/api-service.ts";
import {IMedal, IUserGame, MedalType} from "../../../../common/utils/http/IResponse.ts";
import useLoading from "../../../../providers/loadingContext/useLoading.ts";
import {ToastContext} from "../../../../components/CustomToast/ToastProvider.tsx";
import debounce from "lodash/debounce";
import {GAEvents} from "../../../../common/utils/GA4/GAEvents.ts";
import {useMusicContext} from "../../../../providers/musicProvider/music-player-context.tsx";

type Props = {
    isOpen: boolean;
    onClose: () => void;
    equippedMedalList: IMedal[];
    loadEquippedMedalList: () => void;
    gameData?: IUserGame;
};

// 勋章弹窗列表
const MedalDialog: React.FC<Props> = ({isOpen, onClose, equippedMedalList, loadEquippedMedalList, gameData}) => {
    // 主题全局管理器
    const {imgUrls} = useContext(ThemeContext);
    // 全局音乐provider
    const musicManager = useMusicContext();
    // 加载中弹窗
    const {showLoading, hideLoading} = useLoading();
    const {addToast} = useContext(ToastContext);

    // 切换按钮
    const [switchIndex, setSwitchIndex] = React.useState<number>(1)
    // 徽章列表
    const [medalList, setMedalList] = React.useState<IMedal[]>([])
    // 点击切换按钮
    const handleSwitchBtn = (index: number) => {
        musicManager.playButtonSound()
        setSwitchIndex(index)
    }

    // 获取徽章列表
    const loadMedalList = async () => {
        try {
            showLoading();
            const response = await apiService.loadMedalList();
            if (response.code === 200) {
                setMedalList(response.data)
            }
        } finally {
            hideLoading();
        }
    }

    // 显示时获取列表
    useEffect(() => {
        if (isOpen) {
            // GA埋点
            GAEvents.achieveShow()
            loadMedalList()
        }
    }, [isOpen])

    // 设置徽章装备状态
    const setMedalStatus = debounce(async (medal: IMedal) => {
        try {
            musicManager.playButtonSound()
            if (!medal) return;
            if (!medal.hasGot) return;
            showLoading();
            const response = await apiService.equipMedal(medal.id, !medal.hasEquipped)
            if (response.code === 200) {
                addToast({type: 'success', message: response.message})
                // 重载徽章列表
                loadMedalList();
                // 重载已经装备的徽章列表
                loadEquippedMedalList();
            } else {
                addToast({type: 'error', message: response.message})
            }
        } finally {
            hideLoading();
        }
    }, 100)

    // 处理AI对战当前关卡,返回分母要显示的数字
    const handleCurrentLevel = (): number => {
        if (gameData && gameData?.aiChallengeBestStreak) {
            const currentLevel = gameData.aiChallengeBestStreak;
            if (currentLevel > 0 && currentLevel < 5) {
                return 5;
            }

            if (currentLevel >= 5 && currentLevel < 10) {
                return 10;
            }

            if (currentLevel >= 10 && currentLevel < 20) {
                return 20;
            }

            if (currentLevel >= 20 && currentLevel < 50) {
                return 50;
            }

            if (currentLevel >= 50 && currentLevel < 100) {
                return 100;
            }

            if (currentLevel >= 100 && currentLevel < 1000) {
                return 1000;
            }

            if (currentLevel >= 1000 && currentLevel < 10000) {
                return 10000;
            }
            return 1;
        }
        return 1;
    }

    // 处理每日模式当前赢得次数,返回分母要显示的数字
    const handleDailyWinCount = (): number => {
        if (gameData && gameData?.dailyGameWinNum) {
            const currentWins = gameData.dailyGameWinNum ?? 0;
            if (currentWins > 0 && currentWins < 7) {
                return 7;
            }

            if (currentWins >= 7 && currentWins < 30) {
                return 30;
            }

            if (currentWins >= 30 && currentWins < 180) {
                return 180;
            }

            if (currentWins >= 180 && currentWins < 365) {
                return 365;
            }

            if (currentWins >= 365 && currentWins < 730) {
                return 730;
            }

            return 1;
        }
        return 1;
    }
    return (
        <CustomPopup isOpen={isOpen} onClose={onClose}>
            <div className="medal-dialog-container bg-color-white border-radius-10">
                <img className="medal-top-bg-img" alt="pic" src={imgUrls.medalDialogTopBg}/>
                <div
                    className="modal-title height-27 display-flex-row display-flex-align-items-center display-flex-justify-content-center my-roboto-medium color-white">Achievements
                </div>
                <img onClick={onClose} className="modal-close-btn" src={imgUrls.closeBtnWhiteUrl} alt="pic"/>
                {/*顶部三个勋章部分*/}
                <div className="height-208 width-267 margin-left-right-auto position-relative">
                    {/*左侧柱子*/}
                    <div className="height-169 width-80 position-bottom-8 position-absolute">
                        {/*柱子*/}
                        <img className="height-135 width-80 position-absolute position-bottom-0 object-fit-contain"
                             src={imgUrls.medalTopLeftPillar} alt="pic"/>
                        {/*柱子上的文字*/}
                        <div
                            className={`${equippedMedalList.length > 1 ? 'medal-pillar-text' : 'color-grey'} my-roboto-medium font-size-14 width-80 text-center position-absolute position-top-83`}>
                            {equippedMedalList.length > 1 ? equippedMedalList[1].medalName : 'Add'}
                        </div>
                        {/*勋章*/}
                        {equippedMedalList.length > 1 ?
                            <img
                                className="height-60 width-60 img-no-repeat-center-100-height position-absolute position-bottom-108 position-left-10 position-right-10"
                                src={equippedMedalList[1].medalUrl} alt="pic"/>
                            :
                            <img
                                className="height-53 width-53 img-no-repeat-center-100-height position-absolute position-bottom-116 position-left-14 position-right-14"
                                src={imgUrls.medalAddIconWhite} alt="pic"/>}
                    </div>
                    {/*右侧柱子*/}
                    <div className="height-169 width-80 position-bottom-0 position-absolute position-right-0">
                        {/*柱子*/}
                        <img className="height-135 width-80 position-absolute position-bottom-8 object-fit-contain"
                             src={imgUrls.medalTopLeftPillar} alt="pic"/>
                        {/*柱子上的文字*/}
                        <div
                            className={`${equippedMedalList.length > 2 ? 'medal-pillar-text' : 'color-grey'} my-roboto-medium font-size-14 width-80 text-center position-absolute position-bottom-70`}>
                            {equippedMedalList.length > 2 ? equippedMedalList[2].medalName : 'Add'}
                        </div>
                        {/*勋章*/}
                        {equippedMedalList.length > 2 ?
                            <img
                                className="height-60 width-60 img-no-repeat-center-100-height position-absolute position-bottom-116 position-left-10 position-right-16"
                                src={equippedMedalList[2].medalUrl} alt="pic"/> :
                            <img
                                className="height-53 width-53 img-no-repeat-center-100-height position-absolute position-bottom-124 position-left-14 position-right-14"
                                src={imgUrls.medalAddIconWhite} alt="pic"/>}

                    </div>
                    {/*中间柱子*/}
                    <div className="height-196 width-120 position-bottom-0 position-absolute position-left-73">
                        {/*柱子*/}
                        <img className="height-142 width-120 position-absolute position-bottom-0"
                             src={imgUrls.medalTopMiddlePillar} alt="pic"/>
                        {/*柱子上的文字*/}
                        <div
                            className={`${equippedMedalList.length > 0 ? 'medal-pillar-text' : 'color-grey'} my-roboto-medium font-size-14 width-120 text-center position-absolute position-top-126`}>
                            {equippedMedalList.length > 0 ? equippedMedalList[0].medalName : 'Add'}
                        </div>
                        {/*勋章*/}
                        {equippedMedalList.length > 0 ?
                            <img
                                className="height-93 width-93 img-no-repeat-center-100-height position-absolute position-bottom-104 position-left-14 position-right-14"
                                src={equippedMedalList[0].medalUrl} alt="pic"/> :
                            <img
                                className="height-90 width-90 img-no-repeat-center-100-height position-absolute position-bottom-108 position-left-14 position-right-14"
                                src={imgUrls.medalAddIconWhite} alt="pic"/>}
                    </div>
                </div>

                <div
                    className="height-380 width-360 border-radius-10 bg-color-white position-absolute position-bottom-0">
                    {/*可切换按钮*/}
                    <div className="width-306 height-38 margin-left-right-auto display-flex-row">
                        <div onClick={() => handleSwitchBtn(1)}
                             className={`width-153 ${switchIndex === 1 ? 'color-black-custom my-roboto-medium' : 'color-grey my-roboto-regular'} font-size-14 display-flex-row display-flex-align-items-center display-flex-justify-content-center bottom-border-line`}>
                            Common Badges
                        </div>
                        <div onClick={() => handleSwitchBtn(2)}
                             className={`width-153 ${switchIndex === 2 ? 'color-black-custom my-roboto-medium' : 'color-grey my-roboto-regular'}  font-size-14 display-flex-row display-flex-align-items-center display-flex-justify-content-center bottom-border-line`}>
                            Special Badges
                        </div>
                    </div>
                    {/*切换按钮指示器-横线*/}
                    <div className="width-306 margin-left-right-auto display-flex-row margin-top-down-2">
                        <div
                            className={`selected-indicator width-153 ${switchIndex === 1 ? "margin-right-auto" : "margin-left-auto"}`}/>
                    </div>
                    {/*卡片*/}
                    {switchIndex === 1 ?
                        // Common badges
                        <div className="height-325 width-333 display-flex-column margin-left-right-auto">
                            <div className="margin-top-13 height-325 overflow-y-auto display-flex-column">
                                <div
                                    className="display-flex-column height-210 min-height-210 width-333 border-radius-10 bg-color-grey margin-left-right-auto">
                                    {/*标题*/}
                                    <div
                                        className="height-36 margin-top-3 display-flex-row margin-left-right-auto display-flex-justify-content-center display-flex-align-items-center">
                                        <div className="font-size-14 my-roboto-regular color-grey">Win (</div>
                                        <div
                                            className="font-size-14 my-roboto-regular color-blue">{gameData?.aiChallengeBestStreak ?? 0}</div>
                                        <div
                                            className="font-size-14 my-roboto-regular color-grey">/{handleCurrentLevel()})
                                            consecutive
                                            levels.
                                        </div>
                                    </div>
                                    {/*徽章列表*/}
                                    <div className="margin-left-right-auto width-333">
                                        <div
                                            className="width-324 overflow-x-auto display-flex-row margin-left-5 margin-right-6">
                                            {
                                                medalList.map((item) => {
                                                    if (item.grantType === MedalType.CHALLENGE_MODE_ACHIEVEMENT) {
                                                        return (
                                                            <div onClick={() => setMedalStatus(item)}
                                                                 className={`margin-left-3 margin-right-3 width-98 display-flex-column display-flex-align-items-center`}>
                                                                <div
                                                                    className="bg-color-white width-98 height-119 border-radius-6 display-flex-column display-flex-justify-content-center display-flex-align-items-center">
                                                                    <img
                                                                        className={`${item.hasGot ? '' : 'img-grayscale-50-white'} width-73 height-73 img-no-repeat-center-100-height`}
                                                                        src={item.medalUrl} alt="pic"/>
                                                                    <div
                                                                        className={`${item.hasGot ? '' : 'color-grey'} font-size-13 my-roboto-medium margin-top-3`}>
                                                                        {item.medalName}
                                                                    </div>
                                                                </div>
                                                                <div
                                                                    className={`${item.hasGot ? item.hasEquipped ? 'bg-btn-orange' : 'bg-btn-blue-color' : 'bg-color-grey-btn'} width-73 height-29 border-radius-34 display-flex-row display-flex-align-items-center display-flex-justify-content-center color-white my-roboto-medium font-size-14 margin-top-10`}>
                                                                    {item.hasGot ? item.hasEquipped ? 'Unequip' : 'Equip' : 'Locked'}
                                                                </div>
                                                            </div>
                                                        )
                                                    }
                                                })
                                            }
                                        </div>
                                    </div>
                                </div>

                                <div
                                    className="margin-top-13 display-flex-column height-210 min-height-210 width-333 border-radius-10 bg-color-grey margin-left-right-auto">
                                    {/*标题*/}
                                    <div
                                        className="height-36 margin-top-3 display-flex-row margin-left-right-auto display-flex-justify-content-center display-flex-align-items-center">
                                        <div className="font-size-14 my-roboto-regular color-grey">Check in
                                            consecutively for (
                                        </div>
                                        <div
                                            className="font-size-14 my-roboto-regular color-blue">{gameData?.dailyGameWinNum ?? 0}</div>
                                        <div
                                            className="font-size-14 my-roboto-regular color-grey">/{handleDailyWinCount()})
                                            days.
                                        </div>
                                    </div>
                                    {/*徽章列表*/}
                                    <div className="margin-left-right-auto width-333">
                                        <div
                                            className="width-324 overflow-x-auto display-flex-row margin-left-5 margin-right-6">
                                            {
                                                medalList.map((item) => {
                                                    if (item.grantType === MedalType.DAILY_MODE_ACHIEVEMENT) {
                                                        return (
                                                            <div onClick={() => setMedalStatus(item)}
                                                                 className={`margin-left-3 margin-right-3 width-98 display-flex-column display-flex-align-items-center`}>
                                                                <div
                                                                    className="bg-color-white width-98 height-119 border-radius-6 display-flex-column display-flex-justify-content-center display-flex-align-items-center">
                                                                    <img
                                                                        className={`${item.hasGot ? '' : 'img-grayscale-50-white'} width-73 height-73 img-no-repeat-center-100-height`}
                                                                        src={item.medalUrl} alt="pic"/>
                                                                    <div
                                                                        className={`${item.hasGot ? '' : 'color-grey'} font-size-13 my-roboto-medium margin-top-3`}>
                                                                        {item.medalName}
                                                                    </div>
                                                                </div>
                                                                <div
                                                                    className={`${item.hasGot ? item.hasEquipped ? 'bg-btn-orange' : 'bg-btn-blue-color' : 'bg-color-grey-btn'} width-73 height-29 border-radius-34 display-flex-row display-flex-align-items-center display-flex-justify-content-center color-white my-roboto-medium font-size-14 margin-top-10`}>
                                                                    {item.hasGot ? item.hasEquipped ? 'UnEquip' : 'Equip' : 'Locked'}
                                                                </div>
                                                            </div>
                                                        )
                                                    }
                                                })
                                            }
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        :
                        // 特殊徽章列表
                        <div className="height-340 width-306 margin-left-right-auto">
                            <div className="height-334 overflow-y-auto margin-top-3">

                                {medalList.map((item) => {
                                    if (item.grantType !== MedalType.DAILY_MODE_ACHIEVEMENT && item.grantType !== MedalType.CHALLENGE_MODE_ACHIEVEMENT) {
                                        return (
                                            <div onClick={() => setMedalStatus(item)}
                                                 className="height-80 width-306 bottom-border-line display-flex-row display-flex-align-items-center">
                                                <img
                                                    className={`${item.hasGot ? '' : 'img-grayscale-50'} width-53 height-53 img-no-repeat-center-100-height`}
                                                    alt="pic"
                                                    src={item.medalUrl}/>
                                                <div className="display-flex-column width-178 margin-left-6 margin-right-4">
                                                    <div className="my-roboto-bold font-size-15">{item.medalName}</div>
                                                    <div
                                                        className="margin-top-4 my-roboto-regular font-size-11 display-flex-row">
                                                        <div className="color-grey">{item.description}</div>
                                                    </div>
                                                </div>
                                                <div
                                                    className={`${item.hasGot ? item.hasEquipped ? 'bg-btn-orange' : 'bg-btn-blue-color' : 'bg-color-grey-btn'} border-radius-34 height-29 width-73 display-flex-row display-flex-align-items-center display-flex-justify-content-center color-white my-roboto-bold font-size-14`}>
                                                    {item.hasGot ? item.hasEquipped ? 'UnEquip' : 'Equip' : 'Locked'}
                                                </div>
                                            </div>
                                        )
                                    }
                                })
                                }
                            </div>
                        </div>
                    }
                </div>
            </div>
        </CustomPopup>
    )
}

export default MedalDialog;